import { useState, useCallback } from 'react'

import { initRequestParam } from 'constants/constants'

import { useAuthContext } from 'AuthProvider'
import _ from 'lodash'
import { useApiCacheStore } from 'store/apiCacheStore'
import { useCurrentStatusStore } from 'store/currentStatusStore'
import { normalizeDate } from 'utils/convertFromLocalDate'
import { getAPI } from 'utils/getAPI'

import type { AlertMstItem, GetAlertMstItemsItemAlertGetRequest, GetMstItemsItemGetRequest, Item, MstItem, MasterItemUpdateMasterItemUpdatePostRequest, ResponseResult, ResponseUpdate } from 'api-client' 
import type { ErrorResponse } from 'types/types'

export interface AutocompleteItem {
  label: string | null
  orgItemCode: string | null
  itemCode: string | null
}

export interface MstItemSeq extends MstItem{
  id: string
}

export interface MstItemsSeq extends Item{
  list: MstItemSeq[]
}

export interface AlertMstItemSeq extends AlertMstItem {
  id: string
}

export interface AlertMstItemsSeq extends Omit<Item, 'list'> {
  list: AlertMstItemSeq[]
}

interface FetchItemsOptions {
  useCache?: boolean
  forceRefresh?: boolean
}

export const useItem = () => {
  const [items, setItems] = useState<MstItemsSeq>(initRequestParam)
  const [alertItems, setAlertItems] = useState<AlertMstItemsSeq>(initRequestParam)
  const [findItem, setFindItem] = useState<Item>(initRequestParam)
  const [isLoading, setIsLoading] = useState(false)
  const [isAlertItemsLoading, setIsAlertItemsLoading] = useState(false)
  const [isFindItemLoading, setIsFindItemLoading] = useState(false)
  const [result, setResult] = useState<ResponseResult>()
  const [findItemresult, setFindItemResult] = useState<ResponseResult>()
  const [mstItemUpdateResult, setMstItemUpdateResult] = useState<ResponseUpdate | undefined>(undefined)
  const [isUpdating, setIsUpdating] = useState(false)
  const { setCurrentStatuses } = useCurrentStatusStore()
  
  const [autocompleteItems, setAutocompleteItems] = useState<AutocompleteItem[]>([])
  const [itemSearch, setItemSearch] = useState<Item>(initRequestParam)
  const [itemSearchIsLoading, setItemSearchIsLoading] = useState(false)
  const [itemSearchResult, setItemSearchResult] = useState<ResponseResult | undefined>(undefined)
  const { callApiWithCache, cache, clearCache } = useApiCacheStore()
  const { loginUserInfo } = useAuthContext()
  const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())
  if (systemDate) systemDate.setHours(0, 0, 0, 0)

  const fetchItems = useCallback(async (searchParam: GetMstItemsItemGetRequest, options?: FetchItemsOptions) => {
    setItems(initRequestParam)
    setResult(undefined)
    setIsLoading(true)

    try {
      const requestPayloadWithCache = {
        companyId: searchParam.companyId,
      }

      let response
      if (options?.useCache) {
        response = await callApiWithCache(
          '/item',
          requestPayloadWithCache,
          async () => {
            const api = await getAPI()
            if (!api) throw new Error('API initialization failed')
            return api.getMstItemsItemGet(requestPayloadWithCache)
          },
          systemDate,
          options.forceRefresh ?? false
        )
      } else {
        const api = await getAPI()
        if (!api) return undefined
        response = await api.getMstItemsItemGet({
          ...searchParam,
          itemName: searchParam.itemName || undefined,
          orgItemCode: searchParam.orgItemCode || undefined,
          orgSupplierId: searchParam.orgSupplierId || undefined,
          itemCode: searchParam.itemCode || undefined,
          barcode: searchParam.barcode || undefined,
        })
      }

      setItems({
        ...response.data,
        list: _.map(response.data.list, (value) => ({
          ...value,
          id: Math.random().toString(32).substring(2),
        }))
      })
      setResult(response.result)
      if (options?.useCache) {
        return response
      }
      setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
      return response
    } catch (e) {
      const error = e as ErrorResponse
      setResult({
        status: 'error',
        message: error.response && error.response.status === 422 ? '入力値に誤りがあります。' : `商品マスタの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return undefined
    } finally {
      setIsLoading(false)
    }
  }, [setCurrentStatuses, callApiWithCache, systemDate])

  const fetchAlertItems = async (searchParam: GetAlertMstItemsItemAlertGetRequest) => {
    setAlertItems(initRequestParam)
    setResult(undefined)
    setIsAlertItemsLoading(true)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.getAlertMstItemsItemAlertGet({
        ...searchParam,
        orgSupplierId: searchParam.orgSupplierId || undefined,
      })

      setAlertItems({
        ...response.data,
        list: _.map(response.data.list, (value) => ({
          ...value,
          id: Math.random().toString(32).substring(2),
        })),
      })
      setResult(response.result)
      setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
      return response
    } catch (e) {
      const error = e as ErrorResponse
      setResult({
        status: 'error',
        message: error.response && error.response.status === 422 ? '入力値に誤りがあります。' : '商品マスタアラートの取得でエラーが発生しました。',
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return undefined
    } finally {
      setIsAlertItemsLoading(false)
    }
  }

  // 一つの商品を取得したい場合に使用
  const fetchFindItem = async (searchParam: GetMstItemsItemGetRequest) => {
    setFindItem(initRequestParam)
    setFindItemResult(undefined)
    setIsFindItemLoading(true)
    setResult(undefined)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.getMstItemsItemGet({
        companyId: searchParam.companyId,
        itemCode: searchParam.itemCode,
        orgItemCode: searchParam.orgItemCode
      })

      setFindItem(response.data)
      setFindItemResult(response.result)
      return response
    } catch (e) {
      const error = e as ErrorResponse
      setFindItemResult({
        status: 'error',
        message: error.response && error.response.status === 422 ? '入力値に誤りがあります。' : `商品マスタの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return undefined
    } finally {
      setIsFindItemLoading(false)
    }
  }

    const clearItemApiCache = useCallback(() => {
      Object.keys(cache).forEach((key) => {
        if (key.startsWith('/item:')) {
          clearCache(key)
        }
      })
    }, [cache, clearCache])

    // 製品のアップデートに使用されます
    const fetchUpdateItem = async (body: MasterItemUpdateMasterItemUpdatePostRequest): Promise<ResponseUpdate> => {
      setIsUpdating(true);
      setMstItemUpdateResult(undefined)
      setResult(undefined)
      try {
        const api = await getAPI()
        if (!api) throw new Error('API initialization failed')
        const response = await api.masterItemUpdateMasterItemUpdatePost(body);
        
        setResult(response.result)
        setMstItemUpdateResult(response);
        clearItemApiCache()
        
        if (response.result.status !== 'success') {
          const error = new Error(response.result.message || '入力エラーがあります');
          Object.assign(error, {
              ...response
          });
          throw error;
        }
        return response;
      } catch (error: unknown) {
        if (error && typeof error === 'object' && 'result' in error) {
            const apiResponse = error as ResponseUpdate; 
            await new Promise<void>(resolve => {
              setMstItemUpdateResult(apiResponse);
              resolve();
            });
            return await Promise.reject(apiResponse);
        }
        const { response } = error as any;
        const errorResponse = {
            errors: [],
            result: {
                status: 'error',
                message: response && response.status === 422 ? '入力値に誤りがあります。' : `商品マスタの更新でエラーが発生しました。`,
                systemDate: null,
                pageNo: 0,
                systemInfo: null,
            },
            updateCount: null,
            lastUpdateDatetime: null,
        };
        await new Promise<void>(resolve => {
            setMstItemUpdateResult(errorResponse);
            setResult(errorResponse.result)
            resolve();
        });
        return await Promise.reject(errorResponse);
      } finally {
        setIsUpdating(false);
      }
    };

  return {
    result,
    items,
    fetchItems,
    alertItems,
    fetchAlertItems,
    isLoading,
    isAlertItemsLoading,

    findItem,
    fetchFindItem,
    findItemresult,
    isFindItemLoading,

    fetchUpdateItem,
    mstItemUpdateResult,
    isUpdating,
  }
}
